import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';

type ModalOverlayProps = {
  isOpen: boolean;
  onClose?: () => void;
  children: React.ReactNode;
  /** z-index layer — defaults above app sidebar (z-50). */
  zIndexClassName?: string;
  ariaLabel?: string;
};

/**
 * Full-viewport modal backdrop rendered via portal on document.body.
 * Use this instead of `fixed inset-0` inside `#app-main-scroll` — nested scroll
 * containers break viewport-fixed positioning and leave a gap at the top when scrolled.
 */
export const ModalOverlay: React.FC<ModalOverlayProps> = ({
  isOpen,
  onClose,
  children,
  zIndexClassName = 'z-[100]',
  ariaLabel,
}) => {
  useEffect(() => {
    if (!isOpen) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen]);

  if (!isOpen || typeof document === 'undefined') return null;

  return createPortal(
    <div
      className={`fixed inset-0 ${zIndexClassName} flex items-center justify-center bg-black/40 backdrop-blur-[1px] px-4`}
      onClick={onClose}
      role="presentation"
      aria-label={ariaLabel}
    >
      <div onClick={(event) => event.stopPropagation()}>{children}</div>
    </div>,
    document.body
  );
};
